// ============================================================================
// Design tokens for non-CSS consumers (inline SVG, hand-rolled charts, Google
// Maps marker icons, DB-enum → display-style maps).
//
// CW-J Phase 0.5: this file NO LONGER HOLDS COLOUR LITERALS for the shared
// token systems. It READS them from the CSS variables declared in
// `src/design/designs/assets/tcs.css`, which is THE colour authority.
//
// Why this matters beyond tidiness: these values are consumed in JavaScript at
// render time (the facility-health donut, map pin fills). A CSS-only dark mode
// — `.dark { --x: … }` — cannot reach a hex that is frozen into a JS module.
// Reading the live variable is what makes those surfaces themeable at all, and
// it is the reason Phase 0.5 had to land before the Phase 1 migration.
//
// Two parallel systems used together (Option A from the AM README):
//   • RAG (facility health)        → 12px filled circle dot
//   • Sentiment (latest feedback)  → text pill / chip
// Plus markets, urgency tiers, supply categories, PO statuses, and vendor
// coverage badges used across the rest of the app.
// ============================================================================

import type {
  FeedbackCategory,
  FeedbackSentiment,
  RagCategory,
  RagStatus,
} from './types';

// ----- CSS-variable reader --------------------------------------------------
// Returns UPPERCASE HEX, not the raw `rgb(…)` computed value. That is
// deliberate: `map-container.tsx` compares colours by string identity
// (`color === MUTED_COLOR` at :582 and :588 decides pin opacity and z-index),
// and Google Maps `fillColor` is happiest with hex. Returning `rgb(…)` here
// would break pin muting silently, with no type error and no console warning.

const rgbTriplet = /(-?[\d.]+)[\s,]+(-?[\d.]+)[\s,]+(-?[\d.]+)/;

function toHex(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (s.startsWith('#')) return s.toUpperCase();
  const m = rgbTriplet.exec(s);
  if (!m) return null;
  const byte = (v: string) =>
    Math.max(0, Math.min(255, Math.round(Number(v))))
      .toString(16)
      .padStart(2, '0');
  return `#${byte(m[1])}${byte(m[2])}${byte(m[3])}`.toUpperCase();
}

// Cache keyed on the <html> class list so it self-invalidates when Phase 2
// adds/removes `dark`. Without the key a memo would freeze the light palette
// into the dark theme.
let cacheKey: string | null = null;
const cache = new Map<string, string>();

/**
 * Read a colour custom property off :root as uppercase hex.
 * Falls back to the literal `var(--name)` string, which still resolves
 * correctly in any CSS context (inline `style`, SVG `fill`) — so a missing
 * stylesheet degrades to "right colour, wrong string" rather than "no colour".
 */
export function token(name: string): string {
  // 2026-09-10: no DOM at all is the same class of degradation this function
  // already documents, and now reachable — `sop-card-intake.test.tsx` renders a
  // real card to static markup in a node environment to read what a person
  // would SEE, and the market dot on that card resolves a token. Returning the
  // `var()` string keeps that contract ("right colour, wrong string") instead
  // of throwing and taking the whole render with it.
  if (typeof document === 'undefined') return `var(${name})`;
  const key = document.documentElement.className;
  if (key !== cacheKey) {
    cache.clear();
    cacheKey = key;
  }
  const hit = cache.get(name);
  if (hit !== undefined) return hit;

  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  const value = toHex(raw) ?? `var(${name})`;
  cache.set(name, value);
  return value;
}

// ----- RAG dots -------------------------------------------------------------
// Single definition app-wide as of Phase 0.5. `map-colors.ts` re-exports this
// object rather than keeping its own copy — the map pin and the facility dot
// used to disagree (#EF4444 vs #C0271C on the same facility).

export const RAG_COLORS: Record<RagStatus, string> = {
  get Green() {
    return token('--rag-green');
  },
  get Amber() {
    return token('--rag-amber');
  },
  get Red() {
    return token('--rag-red');
  },
};

export const ragLabel: Record<RagStatus, string> = {
  Green: 'Healthy',
  Amber: 'Needs attention',
  Red: 'At risk',
};

// 5-category mapping for AM-owned RAG attribution (2026-05-21).
// Display labels for the dropdown / badges in the Facility Detail Status tab
// and the My Accounts RAG-edit popover. DB CHECK constraint enforces the
// underscored codes; UI shows the human-readable label.
export const RAG_CATEGORY_LABELS: Record<RagCategory, string> = {
  Inspection: 'Inspection',
  Client_Feedback: 'Client feedback',
  Sub_Transition: 'Sub transition',
  Operational: 'Operational',
  Other: 'Other',
};

// ----- Sentiment pills ------------------------------------------------------

export interface SentimentPillStyle {
  fill: string;
  text: string;
  label: string;
}

export const SENTIMENT_STYLES: Record<FeedbackSentiment, SentimentPillStyle> = {
  Positive: {
    get fill() {
      return token('--sent-pos-bg');
    },
    get text() {
      return token('--sent-pos-ink');
    },
    label: 'Positive',
  },
  Neutral: {
    get fill() {
      return token('--sent-neu-bg');
    },
    get text() {
      return token('--sent-neu-ink');
    },
    label: 'Neutral',
  },
  Negative: {
    get fill() {
      return token('--sent-neg-bg');
    },
    get text() {
      return token('--sent-neg-ink');
    },
    label: 'Negative',
  },
  Critical: {
    get fill() {
      return token('--sent-crit-bg');
    },
    get text() {
      return token('--sent-crit-ink');
    },
    label: 'Critical',
  },
};

// ----- Feedback categories --------------------------------------------------
// Display labels (DB stores enum codes with underscores).
export const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  Cleaning_Quality: 'Cleaning quality',
  Equipment: 'Equipment',
  Supplies: 'Supplies',
  Staff_Behavior: 'Staff behavior',
  Safety: 'Safety',
  Security: 'Security',
  Communication: 'Communication',
  Facility_Damage: 'Facility damage',
  Other: 'Other',
};

export const CATEGORY_OPTIONS: Array<{
  value: FeedbackCategory;
  label: string;
}> = (Object.keys(CATEGORY_LABELS) as FeedbackCategory[]).map((value) => ({
  value,
  label: CATEGORY_LABELS[value],
}));

// ----- Markets --------------------------------------------------------------
// ⛔ THE MARKET VOCABULARY DOES NOT LIVE HERE. It lives in `public.markets` and
// reaches the app through `shared/markets.ts` — the market names, their colours
// and the states each one covers, all in one place, all editable without a
// deploy.
//
// What was removed on 2026-09-10, and why it mattered: a six-key vocabulary
// shaped around STATES, its label map, and a loose substring matcher that
// components used to turn a stored market name into one of those keys. Against
// the eleven metros the business actually runs, that matcher resolved three,
// silently gave Colorado Springs the same colour as Denver, and returned null
// for seven — including all four Texas markets, which is 79 of the 209 live
// buildings. Three of its six keys could not be reached from any canonical
// market name at all.
//
// The failure was not that colours were missing. It was that a building set to
// the WRONG market rendered a confident colour while its correctly-set
// neighbours rendered grey, so the screen argued for the wrong answer.

// ----- Urgency tiers (supply requests) --------------------------------------

export type UrgencyTier = 'Emergency' | 'Soon' | 'Planned';

export interface UrgencyStyle {
  fill: string;
  text: string;
  label: string;
}

export const URGENCY_STYLES: Record<UrgencyTier, UrgencyStyle> = {
  Emergency: {
    get fill() {
      return token('--urg-emerg-fill');
    },
    get text() {
      return token('--urg-emerg-text');
    },
    label: 'Emergency',
  },
  Soon: {
    get fill() {
      return token('--urg-soon-fill');
    },
    get text() {
      return token('--urg-soon-text');
    },
    label: 'Soon',
  },
  Planned: {
    get fill() {
      return token('--urg-plan-fill');
    },
    get text() {
      return token('--urg-plan-text');
    },
    label: 'Planned',
  },
};

// ----- Supply categories ----------------------------------------------------

export type SupplyCategory =
  | 'Chemical'
  | 'Paper'
  | 'Equipment'
  | 'Consumable'
  | 'Other';

export const SUPPLY_CATEGORY_COLORS: Record<SupplyCategory, string> = {
  get Chemical() {
    return token('--cat-chem');
  },
  get Paper() {
    return token('--cat-paper');
  },
  get Equipment() {
    return token('--cat-equip');
  },
  get Consumable() {
    return token('--cat-cons');
  },
  get Other() {
    return token('--cat-other');
  },
};

// ============================================================================
// TODO(cw-j phase 2): the three badge systems below still hold literals.
//
// They are deliberately UNCHANGED in Phase 0.5. Unlike everything above, none
// of them is duplicated in another file — they are already single-sourced, just
// single-sourced in TypeScript instead of CSS. They do need to move into
// tcs.css before dark mode ships (a JS literal cannot be themed), but moving
// them now would mean collapsing three visibly-different neutral greys that are
// doing the same job — #F1F5F9 (Draft), #E5E7EB (Closed), #E2E8F0 (Closed) —
// and that is a visible change, i.e. Phase 1c semantic work, not 0.5.
//
// Phase 2 should hoist these into tcs.css as `--badge-*` channels and, at the
// same time, decide whether they collapse onto the four status tokens
// (danger / warn / ok / info) that Phase 1c introduces.
// ============================================================================

// ----- PO / order status ----------------------------------------------------

export type POStatus =
  | 'Draft'
  | 'Approved'
  | 'Sent'
  | 'Acknowledged'
  | 'Shipped'
  | 'Delivered'
  | 'Invoiced'
  | 'Closed'
  | 'Cancelled';

export interface POStatusStyle {
  fill: string;
  text: string;
  label: string;
}

export const PO_STATUS_STYLES: Record<POStatus, POStatusStyle> = {
  Draft: { fill: '#F1F5F9', text: '#475569', label: 'Draft' },
  Approved: { fill: '#E0F2FE', text: '#075985', label: 'Approved' },
  Sent: { fill: '#DBEAFE', text: '#1E40AF', label: 'Sent' },
  Acknowledged: { fill: '#E0E7FF', text: '#3730A3', label: 'Acknowledged' },
  Shipped: { fill: '#FEF3C7', text: '#92400E', label: 'Shipped' },
  Delivered: { fill: '#D1FAE5', text: '#065F46', label: 'Delivered' },
  Invoiced: { fill: '#EDE9FE', text: '#5B21B6', label: 'Invoiced' },
  Closed: { fill: '#E5E7EB', text: '#374151', label: 'Closed' },
  Cancelled: { fill: '#FEE2E2', text: '#991B1B', label: 'Cancelled' },
};

// ----- Vendor coverage ------------------------------------------------------

export type VendorCoverage = 'Local' | 'Regional' | 'National';

export const VENDOR_COVERAGE_STYLES: Record<
  VendorCoverage,
  { fill: string; text: string; label: string }
> = {
  Local: { fill: '#D1FAE5', text: '#065F46', label: 'Local' },
  Regional: { fill: '#DBEAFE', text: '#1E40AF', label: 'Regional' },
  National: { fill: '#EDE9FE', text: '#5B21B6', label: 'National' },
};

// ----- Lifecycle status -----------------------------------------------------
// Pulled out of my-accounts.tsx so the live app's lifecycle pill can share
// the same color logic everywhere it needs to render.

// Base DB statuses + the derived NEW ACCOUNTS lenses (Option D PR-5): Behind
// (overdue, unconfirmed start — RED), Recent (live ≤14d — AMBER watch), and
// Starting soon (planned start ≤14d — accent).
export type FacilityLifecycle =
  | 'Active'
  | 'Onboarding'
  | 'Pending'
  | 'Closed'
  | 'FLAG'
  | 'Behind'
  | 'Recent'
  | 'Starting soon';

export interface LifecycleStyle {
  fill: string;
  text: string;
  label: string;
}

export const LIFECYCLE_STYLES: Record<FacilityLifecycle, LifecycleStyle> = {
  Active: { fill: '#D1FAE5', text: '#065F46', label: 'Active' },
  Onboarding: { fill: '#DBEAFE', text: '#1E40AF', label: 'Onboarding' },
  Pending: { fill: '#FEF3C7', text: '#92400E', label: 'Pending' },
  Closed: { fill: '#E2E8F0', text: '#475569', label: 'Closed' },
  FLAG: { fill: '#FEE2E2', text: '#991B1B', label: 'FLAG' },
  Behind: { fill: '#FEE2E2', text: '#991B1B', label: 'Behind' },
  Recent: { fill: '#FEF3C7', text: '#92400E', label: 'Recent start' },
  'Starting soon': { fill: '#DBEAFE', text: '#1E40AF', label: 'Starting soon' },
};
